import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import { useAuth } from '../contexts/AuthContext';
import './ViewQuotation.css';

const API_BASE_URL = 'http://72.61.235.16:5001/api/v1';

const ViewQuotation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);
  
  // Quotation data
  const [quotation, setQuotation] = useState(null);
  const [report, setReport] = useState(null);
  const [project, setProject] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // If we have projectId from state, fetch by project
      if (location.state?.projectId) {
        const response = await axios.get(`${API_BASE_URL}/quotations/project/${location.state.projectId}`, config);
        if (response.data.success && response.data.data.quotations && response.data.data.quotations.length > 0) {
          const quotationData = response.data.data.quotations[0];
          setQuotation(quotationData);
          
          // Fetch detailed quotation to get report
          const detailResponse = await axios.get(`${API_BASE_URL}/quotations/${quotationData.id}`, config);
          if (detailResponse.data.success) {
            setReport(detailResponse.data.data.report);
          }

          // Fetch project details
          const projectResponse = await axios.get(`${API_BASE_URL}/projects/${location.state.projectId}`, config);
          if (projectResponse.data.success) {
            setProject(projectResponse.data.data.project);
          }
        } else {
          setError('No quotation found for this project');
        }
      } else {
        // Fetch by quotation ID
        const response = await axios.get(`${API_BASE_URL}/quotations/${id}`, config);
        if (response.data.success) {
          setQuotation(response.data.data.quotation);
          setReport(response.data.data.report);

          // Fetch project details
          const projectResponse = await axios.get(`${API_BASE_URL}/projects/${response.data.data.quotation.project_id}`, config);
          if (projectResponse.data.success) {
            setProject(projectResponse.data.data.project);
          }
        } else {
          setError('Quotation not found');
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.message || 'Failed to load quotation');
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Are you sure you want to approve this quotation?')) {
      return;
    }

    try {
      setApproving(true);
      const token = localStorage.getItem('token');
      
      await axios.patch(`${API_BASE_URL}/quotations/${quotation.id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Quotation approved successfully!');
      navigate('/projects');
    } catch (err) {
      console.error('Error approving quotation:', err);
      alert(err.response?.data?.message || 'Failed to approve quotation');
      setApproving(false);
    }
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `₹${num.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  const downloadPDF = () => {
    if (!quotation) return;

    try {
      const pdf = new jsPDF();
      
      // Add title
      pdf.setFontSize(20);
      pdf.setTextColor(17, 74, 166);
      pdf.text('QUOTATION', 20, 30);
      
      // Add project info
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Project: ${project?.name || 'N/A'}`, 20, 50);
      pdf.text(`Location: ${project?.location || 'N/A'}`, 20, 65);
      pdf.text(`Status: ${quotation.approved ? 'CUSTOMER APPROVED' : 'QUOTATION GENERATED'}`, 20, 80);
      pdf.text(`Created: ${formatDate(quotation.created_at)}`, 20, 95);
      
      if (quotation.approved && quotation.approved_at) {
        pdf.text(`Approved: ${formatDate(quotation.approved_at)}`, 20, 110);
      }
      
      // Add site report details if available
      let yPos = quotation.approved ? 125 : 110;
      if (report) {
        pdf.setFontSize(16);
        pdf.setTextColor(17, 74, 166);
        pdf.text('Site Report Details:', 20, yPos);
        
        pdf.setFontSize(12);
        pdf.setTextColor(0, 0, 0);
        pdf.text(`Total Floors: ${report.total_floors}`, 25, yPos + 20);
        pdf.text(`Dimensions: ${report.dimensions || 'N/A'}`, 25, yPos + 35);
        
        if (report.remarks) {
          pdf.text('Remarks:', 25, yPos + 50);
          const splitRemarks = pdf.splitTextToSize(report.remarks, 160);
          pdf.text(splitRemarks, 25, yPos + 65);
          yPos += 80 + (splitRemarks.length * 5);
        } else {
          yPos += 50;
        }

        if (report.images && report.images.length > 0) {
          pdf.text(`Site Images: ${report.images.length} image(s) available`, 25, yPos + 15);
          yPos += 30;
        }
      }
      
      // Add cost breakdown
      pdf.setFontSize(16);
      pdf.setTextColor(17, 74, 166);
      pdf.text('Cost Breakdown:', 20, yPos + 20);
      
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Material Cost: ${formatCurrency(quotation.material_cost)}`, 25, yPos + 40);
      pdf.text(`Labour Cost: ${formatCurrency(quotation.labour_cost)}`, 25, yPos + 55);
      pdf.text(`Transport Cost: ${formatCurrency(quotation.transport_cost)}`, 25, yPos + 70);
      pdf.text(`Other Cost: ${formatCurrency(quotation.other_cost)}`, 25, yPos + 85);
      
      // Add line
      pdf.setLineWidth(0.5);
      pdf.line(25, yPos + 95, 185, yPos + 95);
      
      // Total cost
      pdf.setFontSize(16);
      pdf.setTextColor(17, 74, 166);
      pdf.text(`Total Cost: ${formatCurrency(quotation.total_cost)}`, 25, yPos + 110);
      
      if (quotation.advance_amount > 0) {
        pdf.setFontSize(12);
        pdf.setTextColor(245, 158, 11);
        pdf.text(`Advance Amount: ${formatCurrency(quotation.advance_amount)}`, 25, yPos + 125);
      }

      // Add footer
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text('Generated by Construction Management System', 20, 280);
      pdf.text(`Generated on: ${new Date().toLocaleString()}`, 20, 290);
      
      // Save the PDF
      pdf.save(`quotation-${project?.name || quotation.project_id}-${Date.now()}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading quotation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error-container">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error}</p>
          <button className="btn-primary" onClick={() => navigate('/quotations')}>
            Back to Quotations
          </button>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="page-container">
        <div className="error-container">
          <i className="fas fa-exclamation-circle"></i>
          <p>Quotation not found</p>
          <button className="btn-primary" onClick={() => navigate('/quotations')}>
            Back to Quotations
          </button>
        </div>
      </div>
    );
  }

  const canApprove = user?.role === 'customer' && !quotation.approved;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <button className="btn-back" onClick={() => navigate('/quotations')}>
            <i className="fas fa-arrow-left"></i> Back
          </button>
          <h1 className="page-title">Quotation Details</h1>
          <p className="page-subtitle">
            {project?.name || 'Project'} • {project?.location || 'N/A'} • Created: {formatDate(quotation.created_at)}
          </p>
        </div>
        <div className="header-actions">
          <button className="btn-download" onClick={downloadPDF}>
            <i className="fas fa-download"></i> Download PDF
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="content-grid">
        {/* Project Summary */}
        <div className="details-card">
          <h3 className="card-title">Project Summary</h3>
          <div className="detail-row">
            <span className="detail-label">Project Name</span>
            <span className="detail-value">{project?.name || 'N/A'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Location</span>
            <span className="detail-value">{project?.location || 'N/A'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Status</span>
            <span className={`detail-value status-badge ${quotation.approved ? 'approved' : 'pending'}`}>
              {quotation.approved ? 'CUSTOMER APPROVED' : 'QUOTATION GENERATED'}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Created At</span>
            <span className="detail-value">{formatDate(quotation.created_at)}</span>
          </div>
          {quotation.approved && quotation.approved_at && (
            <div className="detail-row">
              <span className="detail-label">Approved At</span>
              <span className="detail-value">{formatDate(quotation.approved_at)}</span>
            </div>
          )}
        </div>

        {/* Site Report Details */}
        {report && (
          <div className="details-card">
            <h3 className="card-title">Site Report Details</h3>
            <div className="detail-row">
              <span className="detail-label">Total Floors</span>
              <span className="detail-value">{report.total_floors}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Dimensions</span>
              <span className="detail-value">{report.dimensions || 'N/A'}</span>
            </div>
            {report.remarks && (
              <div className="detail-row">
                <span className="detail-label">Remarks</span>
                <span className="detail-value">{report.remarks}</span>
              </div>
            )}
            {report.images && report.images.length > 0 && (
              <div className="detail-row">
                <span className="detail-label">Site Images</span>
                <div className="images-grid">
                  {report.images.map((imagePath, index) => (
                    <div key={index} className="image-item">
                      <img 
                        src={`http://localhost:5000${imagePath}`}
                        alt={`Site image ${index + 1}`}
                        className="site-image"
                        onClick={() => window.open(`http://localhost:5000${imagePath}`, '_blank')}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quotation Breakdown */}
        <div className="details-card quotation-breakdown">
          <h3 className="card-title">Quotation Breakdown</h3>
          <div className="detail-row">
            <span className="detail-label">Material Cost</span>
            <span className="detail-value">{formatCurrency(quotation.material_cost)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Labour Cost</span>
            <span className="detail-value">{formatCurrency(quotation.labour_cost)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Transport Cost</span>
            <span className="detail-value">{formatCurrency(quotation.transport_cost)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Other Cost</span>
            <span className="detail-value">{formatCurrency(quotation.other_cost)}</span>
          </div>
          
          <div className="divider"></div>
          
          <div className="detail-row total-row">
            <span className="detail-label">Total Cost</span>
            <span className="detail-value total-value">{formatCurrency(quotation.total_cost)}</span>
          </div>
          
          {quotation.advance_amount > 0 && (
            <div className="detail-row advance-row">
              <span className="detail-label">Advance Amount</span>
              <span className="detail-value advance-value">{formatCurrency(quotation.advance_amount)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="actions-section">
        {canApprove && (
          <button 
            className="btn-approve"
            onClick={handleApprove}
            disabled={approving}
          >
            {approving ? (
              <>
                <div className="spinner-small"></div>
                Approving...
              </>
            ) : (
              <>
                <i className="fas fa-check"></i>
                Approve Quotation
              </>
            )}
          </button>
        )}
        
        {quotation.approved && (
          <div className="approved-badge">
            <i className="fas fa-check-circle"></i>
            APPROVED
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewQuotation;